/**
 * RÉFÉRENTIELS → FAMILLES — une carte par famille (couleur de repère, grilles, modèles, stock), rangées par glisser,
 * la fenêtre qui crée ou modifie une famille, et la palette des teintes proposées (maquette, onglet `fams`).
 *
 * ⚠️ L'ORDRE DESCEND EN CAISSE : le filtre des familles de la vente et le préréglage « Toutes les familles » suivent
 *    l'ordre posé ici (`sortByRank`, décision utilisateur). Glisser réécrit les rangs, puis relit.
 * ⚠️ UNE FAMILLE A PLUSIEURS GRILLES (décision utilisateur) : un fournisseur taille en XS/S/M/L, un autre en
 *    38/40/42. Elles se choisissent dans une liste avec recherche et multisélection, et s'écrivent AVEC la famille.
 * ⚠️ LE NOM NE SE MODIFIE PAS ENCORE : l'article porte le LIBELLÉ de sa famille, pas sa clé — renommer la ligne
 *    détacherait les articles créés ensuite. Le renommage vient avec la lecture par la clé (tranche suivante), et la
 *    fenêtre le DIT au lieu de proposer un champ qui casserait le rattachement.
 * ⚠️ LA PALETTE EST CELLE QUE LA FENÊTRE PROPOSE : la maquette tenait deux listes sans lien (la carte et les pastilles
 *    de la fenêtre), une teinte ajoutée n'était jamais proposée. Une seule liste ici (`reference_list`, sorte
 *    `family_palette`, semée par la Console).
 */
import { useEffect, useMemo, useRef, useState, type Ref } from 'react';
import {
  checkLabel, confirm, Icon, ModalBackdrop, Picker, rovingRadio, showToast, Trans, useOverlayPanel, useTileDrag,
} from '@orgazone/ui-web';
import {
  api, type ReferentialFamilyEntry, type ReferentialFamilyListing, type ReferentialGridRef, type ReferentialPaletteEntry,
} from '../api';
import { nombre, t } from '../i18n';
import { FacetError, FacetOrphans, FacetReadFailure, rankWrites, reasonOf } from './referential-facets-shared';

/** Le bord et l'encre d'une famille sans couleur posée — ceux de la maquette. */
const NO_COLOR = { fill: 'var(--ink-300)', ink: 'var(--ink-600)' };


/** « Tailles alpha (XS → XL) » — ce que la carte et la fenêtre montrent d'une grille. */
const gridLabel = (g: ReferentialGridRef): string => (g.first && g.last ? `${g.name} (${g.first} → ${g.last})` : g.name);

export function FamiliesTab({ listing, failure, reload }: {
  listing: ReferentialFamilyListing | null;
  failure: string | null;
  reload: () => void;
}) {
  const [dialog, setDialog] = useState<{ family: ReferentialFamilyEntry | null } | null>(null);
  const [menu, setMenu] = useState<{ family: ReferentialFamilyEntry; rect: DOMRect } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rows = useMemo(() => listing?.rows ?? [], [listing]);
  const order = useMemo(() => rows.map((r) => r.code), [rows]);

  /** Réécrit les rangs pour suivre `next`, puis relit — un refus se dit au-dessus des cartes. */
  const writeOrder = async (next: readonly string[]) => {
    const writes = rankWrites(rows, next);
    if (writes.length === 0) return;
    setBusy(true); setError(null);
    try {
      await api.writeMasterData('family', writes, `family:order:${next.join(',').slice(0, 60)}`);
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  const drag = useTileDrag({ keys: order, onReorder: (next) => void writeOrder(next), axis: 'xy', disabled: busy, inPlace: true });

  if (failure) return <FacetReadFailure failure={failure} />;
  if (!listing) return <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture du référentiel…')}</p>;

  /** Déplace une famille d'un cran au clavier — la poignée se manie aussi sans souris. */
  const nudge = (i: number, step: -1 | 1) => {
    const j = i + step;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j]!, next[i]!];
    void writeOrder(next);
  };

  const remove = async (f: ReferentialFamilyEntry) => {
    setMenu(null);
    const ok = await confirm({
      title: t('Supprimer « {v} » ?', { v: f.label }),
      message: t('Aucun article n’utilise cette famille. Elle sera détachée de ses grilles de tailles. L’opération est irréversible.'),
      confirmLabel: t('Supprimer'), cancelLabel: t('Annuler'), tone: 'danger',
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await api.deleteReferentielEntry('family', { code: f.code }, `family:delete:${f.code}`);
      showToast(t('Famille supprimée · {v}', { v: f.label }), 'info');
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  const setActive = async (f: ReferentialFamilyEntry, active: boolean) => {
    setMenu(null);
    setBusy(true); setError(null);
    try {
      await api.writeMasterData('family', [{ code: f.code, isActive: active }], `family:active:${f.code}:${active ? 1 : 0}`);
      showToast(active ? t('Famille réactivée · {v}', { v: f.label }) : t('Famille désactivée · {v}', { v: f.label }));
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  const addOrphan = async (value: string) => {
    setBusy(true); setError(null);
    try {
      await api.createMasterDataOnly('family', { code: value, label: value, sortOrder: order.length + 1 }, `family:create:${value}`);
      const rest = rankWrites(rows, order);
      if (rest.length) await api.writeMasterData('family', rest, `family:arrange:${value}`);
      showToast(t('Famille créée · {v}', { v: value }));
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-title)', color: 'var(--ink-600)' }}>
          <Trans k="<1>{n}</1> famille" vars={{ n: rows.length }}
            parts={[(x) => <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} />
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setDialog({ family: null })} className="oz-ref-new"
          style={{
            height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff',
            fontSize: 'var(--t-title)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: '0 6px 18px -8px var(--teal-500)', whiteSpace: 'nowrap', cursor: 'pointer',
          }}>
          <Icon name="plus" size={16} stroke={2.2} />{t('Nouvelle famille')}
        </button>
      </div>
      {error && <div style={{ marginBottom: 12, borderRadius: 'var(--r-sm)', overflow: 'hidden' }}><FacetError message={error} /></div>}
      {rows.length === 0 && (
        <p style={{ margin: 0, padding: '18px 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Aucune famille dans la liste.')}</p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(288px,1fr))', gap: 13, alignItems: 'stretch' }}>
        {drag.order.map((code) => {
          const f = rows.find((r) => r.code === code);
          if (!f) return null;
          const i = order.indexOf(code);
          return (
            <FamilyCard key={code} family={f} tile={drag.tile(code)} handle={drag.handle(code)} busy={busy}
              onNudge={(step) => nudge(i, step)}
              onMenu={(rect) => setMenu({ family: f, rect })} />
          );
        })}
        {drag.placeholder}
      </div>
      {drag.ghost}
      <FacetOrphans orphans={listing.orphans} busy={busy} onAdd={(v) => void addOrphan(v)} />
      <PaletteCard palette={listing.palette} reload={reload} />
      {menu && (
        <FamilyMenu family={menu.family} rect={menu.rect} onClose={() => setMenu(null)}
          onEdit={() => { setDialog({ family: menu.family }); setMenu(null); }}
          onDelete={() => void remove(menu.family)}
          onActive={(active) => void setActive(menu.family, active)} />
      )}
      {dialog && (
        <FamilyDialog family={dialog.family} listing={listing} order={order}
          onClose={() => setDialog(null)} onSaved={() => { setDialog(null); reload(); }} />
      )}
    </div>
  );
}

/** Une carte de famille : poignée, repère coloré, grilles, modèles et stock. */
function FamilyCard({ family: f, tile, handle, busy, onNudge, onMenu }: {
  family: ReferentialFamilyEntry;
  tile: ReturnType<ReturnType<typeof useTileDrag>['tile']>;
  handle: ReturnType<ReturnType<typeof useTileDrag>['handle']>;
  busy: boolean;
  onNudge: (step: -1 | 1) => void;
  onMenu: (rect: DOMRect) => void;
}) {
  const fill = f.color ?? NO_COLOR.fill;
  const grids = f.grids.length > 0 ? f.grids.map((g) => g.name).join(' · ') : t('Aucune');
  return (
    <div {...tile} style={{
      ...tile.style, background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: 18,
      display: 'flex', flexDirection: 'column', gap: 13, height: '100%', opacity: f.isActive ? tile.style.opacity : 0.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" {...handle} disabled={busy} className="oz-fam-grip"
          aria-label={t('Déplacer « {v} » — flèches pour la ranger', { v: f.label })}
          title={t('Glisser pour réordonner — l’ordre descend en caisse')}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); onNudge(-1); }
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); onNudge(1); }
          }}
          style={{
            ...handle.style, width: 18, alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--ink-300)', marginLeft: -8, border: 'none', background: 'transparent', padding: 0,
          }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="9" cy="5" r="1.6" /><circle cx="15" cy="5" r="1.6" /><circle cx="9" cy="12" r="1.6" />
            <circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="19" r="1.6" /><circle cx="15" cy="19" r="1.6" />
          </svg>
        </button>
        <span style={{
          width: 42, height: 42, borderRadius: 'var(--r-sm)', background: 'var(--paper)', border: `1.5px solid ${fill}`,
          // ⚠️ L'ENCRE EST LA TEINTE ASSOMBRIE : le remplissage est un décor, le picto doit se lire (la maquette sépare les deux).
          color: f.color ? `color-mix(in oklch, ${f.color} 52%, black)` : NO_COLOR.ink,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}><Icon name="tag" size={21} stroke={1.75} /></span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {f.grids.length > 1 ? t('Grilles : {grilles}', { grilles: grids }) : t('Grille : {grille}', { grille: grids })}</span>
        </span>
        <button type="button" aria-label={t('Autres actions sur « {v} »', { v: f.label })} className="oz-ref-more"
          onClick={(e) => onMenu(e.currentTarget.getBoundingClientRect())}
          style={{
            width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-500)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0, cursor: 'pointer',
          }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" aria-hidden>
            <circle cx="12" cy="5" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="19" r="1.4" /></svg>
        </button>
      </div>
      <div style={{ height: 4, borderRadius: 'var(--r-pill)', background: fill, opacity: 0.9 }} />
      <div style={{ display: 'flex', gap: 20, marginTop: 'auto' }}>
        <Stat label={t('Articles')} value={nombre(f.models)} />
        <Stat label={t('En stock')} value={nombre(f.stock)} />
        {(!f.isActive || f.models === 0) && (
          <span style={{
            alignSelf: 'flex-end', marginLeft: 'auto', height: 21, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)',
            color: 'var(--ink-500)', fontSize: 'var(--t-meta)', fontWeight: 600, display: 'inline-flex', alignItems: 'center',
          }}>{f.isActive ? t('Inutilisée') : t('Désactivée')}</span>
        )}
      </div>
    </div>
  );
}

/** Un chiffre de carte, sous son étiquette en capitales de la maquette. */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{label}</span>
      <span style={{ display: 'block', fontSize: 'var(--t-h2)', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{value}</span>
    </span>
  );
}

/**
 * LE MENU D'UNE CARTE — Modifier, et Supprimer tant que rien ne porte la famille ; sinon la raison, et Désactiver.
 * ⚠️ DÉSACTIVER EST UN AJOUT À LA MAQUETTE : sa note promet qu'« une famille utilisée par des articles ne se supprime
 *    pas — elle se désactive », sans offrir le geste. Le promettre sans le donner laisserait l'opérateur sans issue.
 */
function FamilyMenu({ family: f, rect, onClose, onEdit, onDelete, onActive }: {
  family: ReferentialFamilyEntry;
  rect: DOMRect;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onActive: (active: boolean) => void;
}) {
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const item = (label: string, icon: 'edit' | 'trash' | 'ban' | 'eye', onClick: () => void, danger = false, ref?: Ref<HTMLButtonElement>) => (
    <button type="button" ref={ref} onClick={onClick} className="oz-ref-menu-item"
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 36, padding: '0 12px', border: 'none', borderRadius: 'var(--r-xs)',
        background: 'transparent', color: danger ? 'var(--rose-fg)' : 'var(--ink-800)', fontSize: 'var(--t-lead)', fontWeight: 500, textAlign: 'left', cursor: 'pointer',
      }}>
      <Icon name={icon} size={15} stroke={1.9} />{label}
    </button>
  );
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1099 }} />
      <div aria-label={t('Actions sur « {v} »', { v: f.label })}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }}
        style={{
          position: 'fixed', top: rect.bottom + 5, left: Math.min(rect.right - 188, window.innerWidth - 200), width: 188, zIndex: 1100,
          background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 12, boxShadow: '0 16px 40px -12px rgba(12,14,16,.34)',
          padding: 5, animation: 'pop .13s',
        }}>
        {item(t('Modifier'), 'edit', onEdit, false, first)}
        {f.carriers === 0
          ? item(t('Supprimer'), 'trash', onDelete, true)
          : (
            <>
              <p style={{ margin: 0, padding: '7px 12px', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
                {t('Suppression impossible : des articles utilisent cette famille.')}</p>
              {f.isActive ? item(t('Désactiver'), 'ban', () => onActive(false)) : item(t('Réactiver'), 'eye', () => onActive(true))}
            </>
          )}
      </div>
    </>
  );
}

/**
 * LA FENÊTRE D'UNE FAMILLE — nom (à la création), grilles de tailles et couleur de repère.
 * ⚠️ LE VERDICT DU NOM EST CELUI DE LA MAQUETTE : un nom déjà pris bloque, un nom proche avertit (« Ressemble à … ») et
 *    le bouton dit « Enregistrer quand même ».
 */
function FamilyDialog({ family, listing, order, onClose, onSaved }: {
  family: ReferentialFamilyEntry | null;
  listing: ReferentialFamilyListing;
  order: readonly string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const rows = listing.rows;
  const [name, setName] = useState('');
  const [grids, setGrids] = useState<string[]>(() => family?.grids.map((g) => g.name) ?? []);
  // ⚠️ LA TEINTE D'UNE FAMILLE NEUVE EST PRÉ-CHOISIE, ET SE VOIT : la maquette en attribuait une en silence au moment
  //    d'enregistrer. La suivante de la palette, cochée d'avance, se change d'un clic.
  const [color, setColor] = useState<string | null>(() =>
    family ? family.color : (listing.palette[rows.length % Math.max(1, listing.palette.length)]?.color ?? null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { panelRef } = useOverlayPanel({ open: true, onClose, autoFocus: 'first', blockClose: () => saving });

  const verdict = family ? null : checkLabel(name, rows.map((r) => r.label));
  const blocked = verdict !== null && (verdict.etat === 'vide' || verdict.etat === 'exact');
  const saveLabel = verdict?.etat === 'proche' ? t('Enregistrer quand même') : t('Enregistrer');
  const title = family ? t('Modifier « {v} »', { v: family.label }) : t('Nouvelle famille');

  const options = listing.grids
    .filter((g) => g.isActive || grids.includes(g.name))
    .map((g) => ({ value: g.name, label: gridLabel(g) }));

  const save = async () => {
    if (blocked || saving) return;
    setSaving(true); setError(null);
    try {
      if (family) {
        await api.writeMasterData('family', [{ code: family.code, color, sizeGrids: grids }], `family:edit:${family.code}`);
        showToast(t('Famille modifiée · {v}', { v: family.label }));
      } else {
        const v = verdict!.etat === 'vide' ? '' : verdict!.valeur;
        await api.createMasterDataOnly('family', { code: v, label: v, color, sizeGrids: grids, sortOrder: order.length + 1 }, `family:create:${v}`);
        // ⚠️ Une liste jamais rangée est numérotée, sans quoi la nouvelle famille — rangée — passerait devant elle.
        const rest = rankWrites(rows, order);
        if (rest.length) await api.writeMasterData('family', rest, `family:arrange:${v}`);
        showToast(t('Famille créée · {v}', { v }));
      }
      onSaved();
    } catch (e) {
      setError(reasonOf(e));
      setSaving(false);
    }
  };

  const field = { display: 'block', fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 } as const;
  return (
    <ModalBackdrop onBackdrop={saving ? undefined : onClose}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} style={{
        position: 'relative', width: 472, maxWidth: '94vw', background: 'var(--paper)', borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--sh-modal)', overflow: 'hidden', animation: 'pop .18s cubic-bezier(.2,.7,.3,1)',
      }}>
        <div style={{ height: 3, background: 'linear-gradient(90deg,var(--teal-500),var(--teal-300))' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-h3)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
          <button type="button" onClick={onClose} disabled={saving} aria-label={t('Fermer')}
            style={{ width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0, cursor: 'pointer' }}>
            <Icon name="x" size={16} stroke={2} />
          </button>
        </div>
        <div style={{ padding: '20px 22px' }}>
          <div style={{ marginBottom: 15 }}>
            <label htmlFor="ref-family-name" style={field}>{t('Nom de la famille')}</label>
            {family ? (
              <>
                <div id="ref-family-name" style={{
                  height: 40, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)',
                  color: 'var(--ink-900)', fontSize: 'var(--t-title)', display: 'flex', alignItems: 'center',
                }}>{family.label}</div>
                <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 6, lineHeight: 1.5 }}>
                  {t('Le nom fixe le rattachement des articles : il se modifiera quand ils désigneront leur famille par sa clé.')}</span>
              </>
            ) : (
              <>
                <input id="ref-family-name" value={name} placeholder={t('Chemises')} onChange={(e) => { setName(e.target.value); setError(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void save(); } }}
                  className="oz-ref-input"
                  style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-title)', outline: 'none', boxSizing: 'border-box' }} />
                {verdict?.etat === 'exact' && (
                  <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--amber-fg)', fontWeight: 600, marginTop: 6 }}>
                    {t('« {v} » existe déjà.', { v: verdict.candidat })}</span>
                )}
                {verdict?.etat === 'proche' && (
                  <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--amber-fg)', fontWeight: 600, marginTop: 6 }}>
                    {t('Ressemble à « {v} » — {pct} %.', { v: verdict.candidat, pct: verdict.pourcent })}</span>
                )}
              </>
            )}
          </div>
          <div style={{ marginBottom: 15 }}>
            <span id="ref-family-grids-label" style={field}>{t('Grilles de tailles')}</span>
            <Picker id="ref-family-grids" multiple variant="field" label={t('Grilles de tailles')} allLabel={t('Aucune')}
              options={options} value={grids} onChange={setGrids} chipsMax={3}
              searchPlaceholder={t('Rechercher une grille…')} />
            <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 7, lineHeight: 1.5 }}>
              {t('Une famille peut en employer plusieurs : un fournisseur taille en lettres, un autre en chiffres. La première est proposée d’abord à la création d’un article.')}</span>
          </div>
          <div>
            <span id="ref-family-color-label" style={field}>{t('Couleur de repère')}</span>
            {listing.palette.length === 0 ? (
              <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
                {t('La palette est vide : ajoutez une teinte sous les cartes.')}</span>
            ) : (
              <div role="radiogroup" aria-labelledby="ref-family-color-label" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {listing.palette.map((p, i) => {
                  const on = (color ?? '').toLowerCase() === p.color.toLowerCase();
                  const anyOn = listing.palette.some((x) => x.color.toLowerCase() === (color ?? '').toLowerCase());
                  return (
                    <button key={p.code} type="button" role="radio" aria-checked={on}
                      {...rovingRadio({ index: i, count: listing.palette.length, checked: on, anyChecked: anyOn, onPick: (k) => setColor(listing.palette[k]!.color) })}
                      aria-label={t('Choisir la teinte « {v} »', { v: p.label })} title={p.label}
                      onClick={() => setColor(p.color)}
                      style={{ width: 34, height: 34, borderRadius: 'var(--r-ctrl)', background: p.color, border: `2.5px solid ${on ? 'var(--ink-900)' : 'transparent'}`, padding: 0, flexShrink: 0, cursor: 'pointer' }} />
                  );
                })}
              </div>
            )}
            <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 9, lineHeight: 1.5 }}>
              {t('Ce repère est repris dans les listes, la fiche article et jusqu’en caisse. Aucune teinte d’alerte n’est proposée.')}</span>
          </div>
        </div>
        {error && <FacetError message={error} />}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={saving}
            style={{ height: 40, padding: '0 18px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-title)', fontWeight: 600, cursor: 'pointer' }}>
            {t('Annuler')}</button>
          <button type="button" onClick={() => void save()} disabled={blocked || saving} aria-disabled={blocked || saving}
            style={{
              height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-title)', fontWeight: 700,
              background: blocked ? 'var(--ink-200)' : 'var(--teal-500)', color: blocked ? 'var(--ink-400)' : '#fff',
              cursor: blocked || saving ? 'not-allowed' : 'pointer',
            }}>{saveLabel}</button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

/**
 * LA PALETTE — les teintes que la fenêtre d'une famille propose, et combien de familles portent chacune.
 * ⚠️ UNE TEINTE PORTÉE NE SE RETIRE PAS : le producteur le refuse, et l'écran n'offre la corbeille qu'à une teinte libre.
 * ⚠️ LA COULEUR EST L'IDENTITÉ D'UNE TEINTE (son code est l'hexadécimal) : deux teintes de même couleur sous deux noms
 *    seraient deux pastilles indiscernables dans la fenêtre — le serveur refuse la seconde, et dit pourquoi.
 */
function PaletteCard({ palette, reload }: { palette: readonly ReferentialPaletteEntry[]; reload: () => void }) {
  const [hex, setHex] = useState('#55a8d6');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    const v = label.replace(/\s+/g, ' ').trim();
    if (!v) return;
    if (checkLabel(v, palette.map((p) => p.label)).etat === 'exact') { setError(t('La teinte « {v} » existe déjà.', { v })); return; }
    setBusy(true); setError(null);
    try {
      await api.createMasterDataOnly('reference_list', {
        kind: 'family_palette', code: hex.slice(1).toLowerCase(), label: v, color: hex.toUpperCase(), sortOrder: palette.length + 1,
      }, `palette:create:${hex}`);
      setLabel('');
      showToast(t('Teinte ajoutée · {v}', { v }));
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  const remove = async (p: ReferentialPaletteEntry) => {
    const ok = await confirm({
      title: t('Retirer la teinte « {v} » ?', { v: p.label }), message: t('Elle ne sera plus proposée à la création d’une famille.'),
      confirmLabel: t('Retirer'), cancelLabel: t('Annuler'), tone: 'danger',
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await api.deleteReferentielEntry('reference_list', { kind: 'family_palette', code: p.code }, `palette:delete:${p.code}`);
      showToast(t('Teinte retirée · {v}', { v: p.label }), 'info');
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  const input = { height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-title)', color: 'var(--ink-900)', outline: 'none' } as const;
  return (
    <div style={{ marginTop: 22, maxWidth: 640 }}>
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 170, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Palette de couleurs')}</span>
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Proposée à la création d’une famille — iso-chroma, hors vocabulaire d’alerte')}</span>
        </div>
        <div style={{ display: 'flex', gap: 9, padding: '13px 18px', borderBottom: '1px solid var(--ink-100)', background: 'var(--ink-50)', alignItems: 'center' }}>
          <input type="color" value={hex} onChange={(e) => setHex(e.target.value)} aria-label={t('Choisir la teinte')}
            style={{ width: 44, height: 38, padding: 2, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', cursor: 'pointer' }} />
          <input value={label} placeholder={t('Nom de la teinte — ex. Océan')} aria-label={t('Nom de la teinte')}
            onChange={(e) => { setLabel(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }}
            className="oz-ref-input" style={{ ...input, flex: 1, minWidth: 0 }} />
          <button type="button" disabled={busy} onClick={() => void add()} className="oz-ref-add"
            style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: busy ? 'default' : 'pointer' }}>
            {t('Ajouter')}</button>
        </div>
        <FacetError message={error} />
        {palette.length === 0 && (
          <p style={{ margin: 0, padding: '16px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Aucune teinte dans la palette.')}</p>
        )}
        {palette.map((p) => (
          <div key={p.code} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px', minHeight: 48, borderBottom: '1px solid var(--ink-100)' }}>
            <span style={{ width: 24, height: 24, borderRadius: 'var(--r-xs)', background: p.color, flexShrink: 0, boxShadow: 'inset 0 0 0 1px rgba(12,14,16,.08)' }} />
            <span style={{ minWidth: 90, fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>{p.label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{p.color.toUpperCase()}</span>
            <span style={{ flex: 1, textAlign: 'right', fontSize: 'var(--t-body)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
              {p.families > 0 ? t('{n} famille', { n: p.families }) : t('libre')}</span>
            {p.families === 0 ? (
              <button type="button" aria-label={t('Retirer la teinte « {v} »', { v: p.label })} disabled={busy} onClick={() => void remove(p)} className="oz-ref-trash"
                style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: busy ? 'default' : 'pointer' }}>
                <Icon name="trash" size={14} stroke={2} />
              </button>
            ) : (
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{t('portée par une famille')}</span>
            )}
          </div>
        ))}
      </div>
      <p style={{ margin: '12px 2px 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        {t('Semée par la Console à la création de la base, à compléter ici. Une famille utilisée par des articles ne se supprime pas — elle se désactive.')}</p>
    </div>
  );
}
